import {
    ScreeningGeneralSubRouteType,
    ScreeningSubRouteType,
    TranslationsSubRouteType,
    UserSubRouteType,
    RouteType,
} from 'app/routing/route-type';
import { Type } from '@angular/core';
import { DefaultExport, Route, Routes } from '@angular/router';
import { SessionService } from 'app/services/session.service';
import { adminProdGuard } from 'app/guards/admin-prod.guard';

interface RouteOptions {
    type: RouteType | string;
    loadComponent: () => Promise<DefaultExport<Type<unknown>>>;
    title: string;
    pathMatch?: string;
    children?: Routes;
    extraGuards?: Route['canActivate'];
}

const route = ({ type, loadComponent, title, pathMatch = 'full', children, extraGuards }: RouteOptions) => {
    return {
        path: type,
        loadComponent,
        pathMatch,
        canActivate: [SessionService, ...(extraGuards ?? [])],
        children,
        title: `Gem | ${title}`,
    } as Route;
};

export const routes: Routes = [
    { path: '', redirectTo: RouteType.login, pathMatch: 'full' },
    route({ type: RouteType.login, loadComponent: () => import('app/components/login/login.component'), title: 'Login' }),
    route({
        type: `:country/${RouteType.users}`,
        loadComponent: () => import('app/components/sitly-users/sitly-users.component'),
        title: 'Search',
    }),
    route({
        type: `:country/${RouteType.users}/:userId`,
        loadComponent: () => import('app/components/user-profile/user-profile.component'),
        title: 'Profile',
        pathMatch: 'prefix',
        children: [
            { path: '', redirectTo: UserSubRouteType.profile, pathMatch: 'full' },
            route({ type: '**', loadComponent: () => import('app/components/user-profile/user-profile.component'), title: 'Profile' }),
        ],
    }),
    route({
        type: `:country/${RouteType.screening}`,
        loadComponent: () => import('app/components/screening/screening.component'),
        title: 'Grey list',
        pathMatch: 'prefix',
        children: [
            { path: '', redirectTo: ScreeningSubRouteType.general, pathMatch: 'full' },
            route({
                type: ScreeningSubRouteType.general,
                loadComponent: () => import('app/components/screening/general/general.component'),
                title: 'Grey list',
                pathMatch: 'prefix',
                children: [
                    { path: '', redirectTo: ScreeningGeneralSubRouteType.greyList, pathMatch: 'full' },
                    route({
                        type: ScreeningGeneralSubRouteType.greyList,
                        loadComponent: () => import('app/components/screening/general/general.component'),
                        title: 'Grey list',
                    }),
                    route({
                        type: ScreeningGeneralSubRouteType.blackList,
                        loadComponent: () => import('app/components/screening/general/general.component'),
                        title: 'Black list',
                    }),
                    route({
                        type: ScreeningGeneralSubRouteType.suspects,
                        loadComponent: () => import('app/components/screening/general/general.component'),
                        title: 'Suspects',
                    }),
                    route({
                        type: ScreeningGeneralSubRouteType.quarantined,
                        loadComponent: () => import('app/components/screening/general/general.component'),
                        title: 'Quarantined',
                    }),
                ],
            }),
            route({
                type: ScreeningSubRouteType.personalData,
                loadComponent: () => import('app/components/screening/personal-data/personal-data.component'),
                title: 'Personal data',
            }),
            route({
                type: ScreeningSubRouteType.sensitivePhrases,
                loadComponent: () => import('app/components/screening/sensitive-phrases/sensitive-phrases.component'),
                title: 'Phrases',
            }),
            route({
                type: ScreeningSubRouteType.exclusions,
                loadComponent: () => import('app/components/screening/exclusions/exclusions.component'),
                title: 'Exclusions',
            }),
        ],
    }),
    route({
        type: `:country/${RouteType.subscriptions}`,
        loadComponent: () => import('app/components/subscriptions/subscriptions.component'),
        title: 'Subscriptions',
    }),
    route({
        type: `:country/${RouteType.feedbacks}`,
        loadComponent: () => import('app/components/feedbacks/feedbacks.component'),
        title: 'User feedback',
    }),
    route({ type: RouteType.settings, loadComponent: () => import('app/components/settings/settings.component'), title: 'Settings' }),
    route({
        type: RouteType.translations,
        loadComponent: () => import('app/components/translations/translations.component'),
        title: 'Translations',
        pathMatch: 'prefix',
        children: [
            { path: '', redirectTo: TranslationsSubRouteType.translate, pathMatch: 'full' },
            route({
                type: TranslationsSubRouteType.translate,
                loadComponent: () => import('app/components/translations/translate/translate.component'),
                title: 'Translations',
            }),
            route({
                type: TranslationsSubRouteType.add,
                loadComponent: () => import('app/components/translations/add/add.component'),
                title: 'Add translations',
            }),
            route({
                type: TranslationsSubRouteType.publish,
                loadComponent: () => import('app/components/translations/publish/publish.component'),
                title: 'Publish translations',
                extraGuards: [adminProdGuard],
            }),
            route({
                type: TranslationsSubRouteType.groups,
                loadComponent: () => import('app/components/translations/groups/groups.component'),
                title: 'Translations group',
            }),
        ],
    }),
    route({
        type: RouteType.addGemUser,
        loadComponent: () => import('app/components/settings/add-gem-user/add-gem-user.component'),
        title: 'Add a GEM user',
    }),
    route({ type: '**', loadComponent: () => import('app/components/not-found/not-found.component'), title: 'Page not found!' }),
];
